import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Send, RefreshCw, Home, MessageCircle, Loader2, MapPin } from "lucide-react";
import {
  CHATBOT_PROGRAMS, CHATBOT_FALLBACK, MAIN_MENU, INDIVIDU_CATEGORIES,
  LEMBAGA_TYPES, MODES, normalizeWaLink, CONTACT_TOPICS, FAQ_ITEMS, waLinkWithText, PROGRAM_CATEGORIES,
} from "@/lib/chatbotMeta";
import { PARTICIPANT_TYPES, CLASS_TYPES, REG_MODES, GENDERS, PROGRAMS, isMinor } from "@/lib/registrationMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-sm focus:border-[#f97316] focus:ring-2 focus:ring-[#f97316]/20 focus:outline-none";

function Bubble({ from, text, link }) {
  const bot = from === "bot";
  return (
    <div className={`flex ${bot ? "justify-start" : "justify-end"}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${bot ? "bg-white border border-slate-200 text-slate-800 rounded-tl-sm" : "bg-[#0f2847] text-white rounded-tr-sm"}`}>
        {text.split("\n").map((line, j) => (
          <p key={j} className={line.startsWith("*") && line.endsWith("*") ? "font-semibold" : ""}>{line.replace(/^\*|\*$/g, "")}</p>
        ))}
        {link && (
          <a href={link.href} target="_blank" rel="noreferrer" data-testid={link.testid}
            className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium bg-[#f97316] hover:bg-[#ea580c] text-white px-3 py-1.5 rounded-lg">
            {link.icon} {link.label}
          </a>
        )}
      </div>
    </div>
  );
}

function LeadForm({ type, program, consentText, onSubmit, submitting }) {
  const isL = type === "lembaga";
  const [f, setF] = useState({
    full_name: "", institution_name: "", institution_type: LEMBAGA_TYPES[0], contact_person: "",
    position: "", phone: "", email: "", category: INDIVIDU_CATEGORIES[0],
    program_interest: program ? CHATBOT_PROGRAMS[program]?.label : "", mode: MODES[0],
    desired_schedule: "", learning_goal: "", participant_count: "", implementation_plan: "",
    notes: "", consent: false,
  });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const submit = (e) => { e.preventDefault(); onSubmit({ ...f, prospect_type: type }); };
  return (
    <form onSubmit={submit} className="space-y-2.5" data-testid="chatbot-lead-form">
      {isL ? (
        <>
          <input className={inputCls} placeholder="Nama Lembaga/Perusahaan *" value={f.institution_name} onChange={set("institution_name")} maxLength={200} required data-testid="lead-institution" />
          <select className={inputCls} value={f.institution_type} onChange={set("institution_type")} data-testid="lead-institution-type">{LEMBAGA_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
          <input className={inputCls} placeholder="Nama Kontak/PIC" value={f.contact_person} onChange={set("contact_person")} maxLength={200} data-testid="lead-contact" />
          <input className={inputCls} placeholder="Jabatan" value={f.position} onChange={set("position")} maxLength={200} data-testid="lead-position" />
        </>
      ) : (
        <>
          <input className={inputCls} placeholder="Nama Lengkap *" value={f.full_name} onChange={set("full_name")} maxLength={200} required data-testid="lead-name" />
          <select className={inputCls} value={f.category} onChange={set("category")} data-testid="lead-category">{INDIVIDU_CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
        </>
      )}
      <input className={inputCls} placeholder="Nomor WhatsApp *" value={f.phone} onChange={set("phone")} maxLength={30} required data-testid="lead-phone" />
      <input className={inputCls} placeholder="Email (opsional)" value={f.email} onChange={set("email")} maxLength={200} data-testid="lead-email" />
      <input className={inputCls} placeholder="Program yang diminati" value={f.program_interest} onChange={set("program_interest")} maxLength={200} data-testid="lead-program" />
      <select className={inputCls} value={f.mode} onChange={set("mode")} data-testid="lead-mode">{MODES.map((m) => <option key={m}>{m}</option>)}</select>
      {isL ? (
        <>
          <input className={inputCls} placeholder="Jumlah calon peserta (opsional)" value={f.participant_count} onChange={set("participant_count")} maxLength={200} data-testid="lead-participants" />
          <input className={inputCls} placeholder="Rencana pelaksanaan (opsional)" value={f.implementation_plan} onChange={set("implementation_plan")} maxLength={1000} data-testid="lead-plan" />
        </>
      ) : (
        <>
          <input className={inputCls} placeholder="Jadwal diinginkan (opsional)" value={f.desired_schedule} onChange={set("desired_schedule")} maxLength={200} data-testid="lead-schedule" />
          <textarea className={inputCls} rows={2} placeholder="Tujuan belajar / catatan (opsional)" value={f.learning_goal} onChange={set("learning_goal")} maxLength={1000} data-testid="lead-goal" />
        </>
      )}
      {isL && <textarea className={inputCls} rows={2} placeholder="Catatan kebutuhan" value={f.notes} onChange={set("notes")} maxLength={1000} data-testid="lead-notes" />}
      <label className="flex items-start gap-2 text-xs text-slate-600">
        <input type="checkbox" checked={f.consent} onChange={(e) => setF((p) => ({ ...p, consent: e.target.checked }))} required className="mt-0.5" data-testid="lead-consent" />
        {consentText || "Saya bersedia dihubungi oleh Admin IEC Mustikajaya mengenai program yang saya pilih."}
      </label>
      <button type="submit" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 bg-[#f97316] hover:bg-[#ea580c] text-white font-medium px-4 py-2.5 rounded-lg text-sm" data-testid="lead-submit">
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Kirim
      </button>
    </form>
  );
}

function RegForm({ consentText, onSubmit, submitting }) {
  const [f, setF] = useState({
    full_name: "", nickname: "", participant_type: "anak", guardian_name: "", phone: "",
    guardian_phone: "", email: "", gender: "", birth_date: "", institution: "", class_or_job: "",
    program_interest: "", class_type: "", mode: "", desired_schedule: "", learning_goal: "",
    source: "Chatbot", notes: "", consent: false,
  });
  const [other, setOther] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const minor = isMinor(f.participant_type);
  const submit = (e) => { e.preventDefault(); onSubmit(f); };
  return (
    <form onSubmit={submit} className="space-y-2.5" data-testid="chatbot-reg-form">
      <select className={inputCls} value={f.participant_type} onChange={set("participant_type")} data-testid="reg-participant-type">
        {PARTICIPANT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
      </select>
      <input className={inputCls} placeholder="Nama Lengkap Calon Siswa *" value={f.full_name} onChange={set("full_name")} maxLength={200} required data-testid="reg-full-name" />
      <input className={inputCls} placeholder="Nama Panggilan (opsional)" value={f.nickname} onChange={set("nickname")} maxLength={200} data-testid="reg-nickname" />
      {minor && <input className={inputCls} placeholder="Nama Orang Tua / Wali" value={f.guardian_name} onChange={set("guardian_name")} maxLength={200} data-testid="reg-guardian-name" />}
      <input className={inputCls} placeholder="Nomor WhatsApp *" value={f.phone} onChange={set("phone")} maxLength={30} required data-testid="reg-phone" />
      {minor && <input className={inputCls} placeholder="WhatsApp Orang Tua/Wali (jika beda)" value={f.guardian_phone} onChange={set("guardian_phone")} maxLength={30} data-testid="reg-guardian-phone" />}
      <input className={inputCls} placeholder="Email (opsional)" value={f.email} onChange={set("email")} maxLength={200} data-testid="reg-email" />
      <div className="grid grid-cols-2 gap-2.5">
        <select className={inputCls} value={f.gender} onChange={set("gender")} data-testid="reg-gender"><option value="">Jenis Kelamin</option>{GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}</select>
        <input type="date" className={inputCls} value={f.birth_date} onChange={set("birth_date")} data-testid="reg-birth-date" />
      </div>
      <input className={inputCls} placeholder="Sekolah / Kampus / Instansi (opsional)" value={f.institution} onChange={set("institution")} maxLength={200} data-testid="reg-institution" />
      <input className={inputCls} placeholder="Kelas / Pekerjaan (opsional)" value={f.class_or_job} onChange={set("class_or_job")} maxLength={200} data-testid="reg-class-or-job" />
      <select className={inputCls} value={other ? "__lainnya__" : f.program_interest} data-testid="reg-program"
        onChange={(e) => { const v = e.target.value; if (v === "__lainnya__") { setOther(true); setF((p) => ({ ...p, program_interest: "" })); } else { setOther(false); setF((p) => ({ ...p, program_interest: v })); } }}>
        <option value="">— Pilih Program * —</option>
        {PROGRAMS.map((p) => <option key={p} value={p}>{p}</option>)}
        <option value="__lainnya__">Lainnya…</option>
      </select>
      {other && <input className={inputCls} placeholder="Tulis nama program *" value={f.program_interest} onChange={set("program_interest")} maxLength={200} data-testid="reg-program-other" required />}
      <div className="grid grid-cols-2 gap-2.5">
        <select className={inputCls} value={f.class_type} onChange={set("class_type")} data-testid="reg-class-type"><option value="">Jenis Kelas</option>{CLASS_TYPES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}</select>
        <select className={inputCls} value={f.mode} onChange={set("mode")} data-testid="reg-mode"><option value="">Metode</option>{REG_MODES.map((m) => <option key={m} value={m}>{m}</option>)}</select>
      </div>
      <input className={inputCls} placeholder="Waktu belajar diinginkan (opsional)" value={f.desired_schedule} onChange={set("desired_schedule")} maxLength={200} data-testid="reg-schedule" />
      <textarea className={inputCls} rows={2} placeholder="Tujuan belajar / catatan (opsional)" value={f.learning_goal} onChange={set("learning_goal")} maxLength={1000} data-testid="reg-goal" />
      <label className="flex items-start gap-2 text-xs text-slate-600">
        <input type="checkbox" checked={f.consent} onChange={(e) => setF((p) => ({ ...p, consent: e.target.checked }))} required className="mt-0.5" data-testid="reg-consent" />
        {consentText || "Calon siswa / wali menyetujui untuk dihubungi Admin IEC Mustikajaya."}
      </label>
      <button type="submit" disabled={submitting} className="w-full inline-flex items-center justify-center gap-2 bg-[#f97316] hover:bg-[#ea580c] text-white font-medium px-4 py-2.5 rounded-lg text-sm" data-testid="reg-submit">
        {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Kirim Pendaftaran
      </button>
    </form>
  );
}

export default function Chatbot() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msgs, setMsgs] = useState([]);
  const [form, setForm] = useState(null);
  const [choices, setChoices] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const scrollRef = useRef(null);
  const embed = new URLSearchParams(window.location.search).get("embed") === "1";

  useEffect(() => {
    axios.get(`${API}/public/chatbot`).then(({ data }) => {
      setCfg(data);
      const init = [{ from: "bot", text: data.welcome_message }];
      if (data.help_message?.trim()) init.push({ from: "bot", text: data.help_message });
      setMsgs(init);
    }).catch(() => setCfg({ is_active: true, welcome_message: "Selamat datang di IEC Mustikajaya." }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [msgs, form]);

  const push = (m) => setMsgs((prev) => [...prev, m]);
  const pushBot = (text, link) => push({ from: "bot", text, link });
  const pushUser = (text) => push({ from: "user", text });

  const showInfo = (key) => {
    const val = cfg?.[key];
    pushBot(val && val.trim() ? val : CHATBOT_FALLBACK);
  };
  const showPrograms = (keys) => {
    keys.forEach((k) => {
      const p = CHATBOT_PROGRAMS[k];
      const desc = cfg?.program_descriptions?.[k];
      pushBot(`*${p.label}*\nSasaran: ${p.target}\n${desc && desc.trim() ? desc : CHATBOT_FALLBACK}`);
    });
  };

  const handleMenu = (item) => {
    pushUser(item.label);
    setForm(null);
    setChoices(null);
    const ans = cfg?.menu_answers?.[item.id];
    if (ans && ans.trim()) pushBot(ans);

    if (item.browse) return openBrowse();
    if (item.info) {
      showInfo(item.info);
      if (item.id === "lokasi" && cfg?.maps_link?.trim()) {
        pushBot("Lihat lokasi kami di peta:", { href: cfg.maps_link, label: "Buka Google Maps", testid: "chatbot-maps-link", icon: <MapPin className="h-3.5 w-3.5" /> });
      }
      return;
    }
    if (item.programs) {
      showPrograms(item.programs);
      if (cfg?.method_info?.trim()) pushBot(`*Metode Pembelajaran*\n${cfg.method_info}`);
      pushBot("Ingin info jadwal & biaya terbaru? Pilih \u201CDaftar Sekarang\u201D untuk mendaftar atau \u201CHubungi Admin\u201D untuk bertanya.");
      return;
    }
    if (item.lembagaForm) {
      if (!ans) showPrograms([item.lembagaForm]);
      pushBot(cfg?.form_intro?.trim() || "Silakan isi data lembaga/perusahaan Anda di bawah ini:");
      return setForm({ type: "lembaga", program: item.lembagaForm });
    }
    if (item.register) {
      pushBot("Sudah siap bergabung? Silakan isi formulir pendaftaran berikut. Tim IEC Mustikajaya akan menghubungi Anda untuk konfirmasi program dan jadwal.");
      return setForm({ type: "register", program: null });
    }
    if (item.contact) return openContact();
    if (item.faq) return openFaq();
  };

  const restart = () => {
    setForm(null);
    setChoices(null);
    const init = [{ from: "bot", text: cfg?.welcome_message || "Selamat datang." }];
    if (cfg?.help_message?.trim()) init.push({ from: "bot", text: cfg.help_message });
    setMsgs(init);
  };

  const goWa = (text) => {
    const href = waLinkWithText(cfg?.whatsapp, text);
    if (!href) { pushBot("Nomor WhatsApp Admin belum dikonfigurasi. Silakan coba menu lain."); return; }
    pushBot("Anda akan diarahkan ke WhatsApp Admin IEC.", { href, label: "Buka WhatsApp Admin", testid: "chatbot-wa-topic", icon: <MessageCircle className="h-3.5 w-3.5" /> });
    try { window.open(href, "_blank", "noopener"); } catch (e) { /* popup blocked; link tetap tersedia di bubble */ }
  };

  const resolveFaq = (f) => {
    if (f.cfgKey) { const v = cfg?.[f.cfgKey]; if (v && v.trim()) return v; }
    return f.a;
  };

  const openBrowse = () => {
    setForm(null);
    pushBot("Silakan pilih kategori program yang ingin Anda ketahui:");
    setChoices(PROGRAM_CATEGORIES.map((it) => ({
      id: `cari-${it.id}`, label: it.label, action: () => handleMenu(it),
    })));
  };

  const openContact = () => {
    setForm(null);
    pushBot("Masih ingin bertanya dulu? Tidak perlu mengisi formulir. Pilih topik pertanyaan dan lanjutkan langsung ke Admin IEC melalui WhatsApp.");
    setChoices(CONTACT_TOPICS.map((t) => ({
      id: `topic-${t.id}`, label: t.label,
      action: () => { pushUser(t.label); setChoices(null); goWa(t.text); },
    })));
  };

  const faqFollowUp = () => setChoices([
    { id: "faq-back", label: "Kembali ke FAQ", action: () => { pushUser("Kembali ke FAQ"); openFaq(); } },
    { id: "faq-daftar", label: "Daftar Sekarang", action: () => { pushUser("Daftar Sekarang"); setChoices(null); pushBot("Sudah siap bergabung? Silakan isi formulir pendaftaran berikut. Tim IEC Mustikajaya akan menghubungi Anda untuk konfirmasi program dan jadwal."); setForm({ type: "register", program: null }); } },
    { id: "faq-admin", label: "Hubungi Admin", action: () => { pushUser("Hubungi Admin"); openContact(); } },
    { id: "faq-home", label: "Menu Utama", action: () => { pushUser("Menu Utama"); restart(); } },
  ]);

  const openFaq = () => {
    setForm(null);
    pushBot("Pertanyaan yang sering diajukan. Pilih salah satu:");
    setChoices(FAQ_ITEMS.map((f, i) => ({
      id: `faq-${i}`, label: f.q,
      action: () => { pushUser(f.q); setChoices(null); pushBot(resolveFaq(f)); faqFollowUp(); },
    })));
  };

  const submitLead = async (payload) => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/chatbot/lead`, { ...payload, referrer: document.referrer || window.location.href });
      pushUser("(Data minat terkirim)");
      pushBot(data.message);
      if (cfg?.closing_message?.trim()) pushBot(cfg.closing_message);
      setForm(null);
    } catch (e) {
      const status = e.response?.status;
      if (status === 429) pushBot("Terlalu banyak permintaan. Silakan coba beberapa saat lagi.");
      else if (status === 400) pushBot(e.response?.data?.detail || "Mohon lengkapi data yang diperlukan.");
      else pushBot(cfg?.error_message?.trim() || "Maaf, data belum berhasil dikirim. Silakan periksa kembali isian Anda atau hubungi Admin melalui WhatsApp.");
    } finally { setSubmitting(false); }
  };

  const submitRegister = async (payload) => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/chatbot/register`, payload);
      pushUser("(Formulir pendaftaran terkirim)");
      pushBot(data.message);
      if (data.reg_no) pushBot(`Nomor Pendaftaran Anda: *${data.reg_no}*`);
      setForm(null);
      setChoices([{ id: "reg-home", label: "Kembali ke Menu Utama", action: () => { pushUser("Menu Utama"); restart(); } }]);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      if (status === 429) pushBot("Terlalu banyak permintaan. Silakan coba beberapa saat lagi.");
      else if (status === 400 || status === 422) pushBot((typeof detail === "string" && detail) || "Mohon lengkapi data yang diperlukan (nama, WhatsApp, program, dan persetujuan).");
      else pushBot(cfg?.error_message?.trim() || "Maaf, pendaftaran belum berhasil dikirim. Silakan periksa kembali isian Anda atau hubungi Admin melalui WhatsApp.");
    } finally { setSubmitting(false); }
  };

  const waHref = normalizeWaLink(cfg?.whatsapp);
  if (loading) return <div className="min-h-screen grid place-items-center bg-slate-100"><Loader2 className="h-8 w-8 animate-spin text-[#0f2847]" /></div>;
  const inactive = cfg && cfg.is_active === false;

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col" data-testid="chatbot-page">
      {!embed && (
        <header className="bg-[#0f2847] text-white px-4 py-3 flex items-center justify-between shadow-md">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-lg bg-[#f97316] grid place-items-center font-bold">IEC</div>
            <div>
              <p className="font-semibold leading-tight">IEC Mustikajaya</p>
              <p className="text-[11px] text-blue-200">Asisten Program Bahasa Inggris</p>
            </div>
          </div>
          <button onClick={restart} className="text-xs inline-flex items-center gap-1 bg-white/10 hover:bg-white/20 px-2.5 py-1.5 rounded-lg" data-testid="chatbot-restart"><RefreshCw className="h-3.5 w-3.5" /> Mulai Ulang</button>
        </header>
      )}

      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 max-w-2xl w-full mx-auto">
        {inactive ? (
          <Bubble from="bot" text={cfg.offhours_message?.trim() || "Chatbot sedang tidak aktif. Silakan hubungi Admin IEC Mustikajaya."} />
        ) : (
          <>
            {msgs.map((m, i) => <Bubble key={i} from={m.from} text={m.text} link={m.link} />)}
            {form && (
              <div className="bg-white border border-slate-200 rounded-2xl p-4 rounded-tl-sm max-w-[95%]">
                {form.type === "register"
                  ? <RegForm consentText={cfg?.consent_text} onSubmit={submitRegister} submitting={submitting} />
                  : <LeadForm type={form.type} program={form.program} consentText={cfg?.consent_text} onSubmit={submitLead} submitting={submitting} />}
              </div>
            )}
            {choices && (
              <div className="flex flex-wrap gap-2" data-testid="chatbot-choices">
                {choices.map((c) => (
                  <button key={c.id} onClick={c.action} data-testid={`choice-${c.id}`}
                    className="text-xs font-medium border border-slate-300 text-slate-700 hover:bg-[#0f2847] hover:text-white hover:border-[#0f2847] px-3 py-1.5 rounded-full transition-colors">
                    {c.label}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {!inactive && (
        <div className="border-t border-slate-200 bg-white p-3 max-w-2xl w-full mx-auto">
          <div className="flex flex-wrap gap-2 mb-2">
            {MAIN_MENU.map((item) => (
              <button key={item.id} onClick={() => handleMenu(item)} data-testid={`menu-${item.id}`}
                className="text-xs font-medium border border-slate-300 text-slate-700 hover:bg-[#0f2847] hover:text-white hover:border-[#0f2847] px-3 py-1.5 rounded-full transition-colors">
                {item.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={restart} className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-medium border border-slate-300 text-slate-600 hover:bg-slate-50 px-3 py-2 rounded-lg" data-testid="chatbot-home"><Home className="h-4 w-4" /> Menu Utama</button>
            {waHref ? (
              <a href={waHref} target="_blank" rel="noreferrer" data-testid="chatbot-whatsapp" className="flex-1 inline-flex items-center justify-center gap-1.5 text-xs font-medium bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-lg"><MessageCircle className="h-4 w-4" /> WhatsApp</a>
            ) : (
              <span className="flex-1 text-center text-[11px] text-slate-400 px-2 py-2 border border-dashed border-slate-300 rounded-lg" data-testid="chatbot-wa-missing">Nomor WhatsApp belum dikonfigurasi Admin</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
